// Save payloads for Estimate Engine Items / Assemblies (sp_SaveEstimateCostItem
// / sp_SaveEstimateCostAssembly, db/30). Pure — shared by the Items /
// Assemblies forms and the paste-from-Excel item importer so the two can
// never drift.
//
// Both procs REPLACE every column on update, so a payload must carry every
// field the row already has. Two consequences fixed here (2026-09-24):
//   • Blank vs zero. A row's SalesBurdenPct / MarkupPct / rounding come back
//     from the database as NULL. The forms used to send `+null` (= 0) for a
//     row opened with Edit, which silently turned "use the estimate's markup
//     settings" into "0% — sell at cost" (computeUnitSellPrice treats any
//     non-null value as a per-line override). NULL now round-trips as NULL.
//   • Fields the form doesn't show (Trade, PricingStatus, SourceVendor,
//     SourceUrl, IsEditable) are passed through instead of reset — an edit
//     used to set every item's Trade back to 'electrical'.
import { calcTypeUnit, patternOptionsForStyle } from "../config/conditionCalcTypes";

export const numOrNull = (v) => (v === "" || v == null || isNaN(+v) ? null : +v);
const numOrZero = (v) => numOrNull(v) ?? 0;

const UTILITY_SOURCES = ["utility_connection", "utility_pipe", "utility_pipe_lf", "utility_fitting", "utility_valve", "utility_support", "utility_tee"];
const PIPE_MATERIAL_SOURCES = ["utility_pipe", "utility_pipe_lf", "utility_fitting", "utility_valve", "utility_support", "utility_tee"];
const PIPE_SIZE_SOURCES = ["utility_pipe", "utility_fitting", "utility_valve", "utility_support", "utility_tee"];

// Quantity2/3Unit aren't editable fields; they're derived from their CalcType
// here, the same way sp_SaveTakeoffConditionLibraryItem derives Condition
// Library's own Quantity1/2/3Unit server-side via fn_ConditionCalcTypeUnit.
export function drawableFieldsPayload(row) {
  return {
    style: row.Style || null,
    quantity1_calc_type: row.Style ? (row.Quantity1CalcType || null) : null,
    quantity2_calc_type: row.Style ? (row.Quantity2CalcType || null) : null,
    quantity3_calc_type: row.Style ? (row.Quantity3CalcType || null) : null,
    quantity2_unit: row.Style ? calcTypeUnit(row.Style, row.Quantity2CalcType) : null,
    quantity3_unit: row.Style ? calcTypeUnit(row.Style, row.Quantity3CalcType) : null,
    default_height_ft: numOrNull(row.DefaultHeightFt),
    default_width_ft: numOrNull(row.DefaultWidthFt),
    default_thickness_in: numOrNull(row.DefaultThicknessIn),
    color: row.Style ? (row.Color || null) : null,
    // Kept only when the chosen value belongs to the current Style's own
    // list — so flipping Linear -> Area doesn't carry a stroke dash over as
    // a bogus fill hatch (it silently drops to the default instead).
    pattern: (patternOptionsForStyle(row.Style) || []).some(o => o.value && o.value === row.Pattern) ? row.Pattern : null,
    shape: row.Style === "count" ? (row.Shape || null) : null,
    syncs_to_criteria_field: row.Style === "count" ? (row.SyncsToCriteriaField || null) : null,
    auto_system: row.Style === "count" ? (row.AutoSystem || null) : null,
    // Room Build-Up (db/70)
    is_room_element: row.IsRoomElement ? 1 : 0,
    element_discipline: row.IsRoomElement ? (row.ElementDiscipline || null) : null,
    element_quantity_basis: row.IsRoomElement ? (row.ElementQuantityBasis || null) : null,
    // OST parity knobs (db/30) — each only meaningful for one Style; a
    // blank input is NULL, and a value left over from a previous Style is
    // dropped rather than carried across.
    grid_tile_w_ft: row.Style === "area" ? numOrNull(row.GridTileWFt) : null,
    grid_tile_h_ft: row.Style === "area" ? numOrNull(row.GridTileHFt) : null,
    grid_gap_in: row.Style === "area" ? numOrNull(row.GridGapIn) : null,
    pattern_spacing_in: (row.Style === "area" || row.Style === "count") && row.Pattern && !["none", "solid"].includes(row.Pattern) ? numOrNull(row.PatternSpacingIn) : null,
    round_to_in: row.Style === "linear" ? numOrNull(row.RoundToIn) : null,
    round_area_to_sf: row.Style === "area" ? numOrNull(row.RoundAreaToSf) : null,
  };
}

function mappingAndPricingPayload(row) {
  return {
    quantity_source_type: row.QuantitySourceType || null, quantity_source_ref: row.QuantitySourceRef || null,
    utility_type: UTILITY_SOURCES.includes(row.QuantitySourceType) ? (row.UtilityType || null) : null,
    pipe_material_key: PIPE_MATERIAL_SOURCES.includes(row.QuantitySourceType) ? (row.PipeMaterialKey || null) : null,
    pipe_nominal_size: PIPE_SIZE_SOURCES.includes(row.QuantitySourceType) ? (row.PipeNominalSize || null) : null,
    quantity_source_slot: row.QuantitySourceSlot || null,
    // Blank = priced through the estimate's own markup settings; never 0.
    sales_burden_pct: numOrNull(row.SalesBurdenPct),
    markup_pct: numOrNull(row.MarkupPct),
    price_round_increment: numOrNull(row.PriceRoundIncrement),
    price_round_offset: numOrNull(row.PriceRoundOffset),
  };
}

export function itemSavePayload(row) {
  return {
    item_id: row.ItemID ?? null, code: row.Code || null, category_id: row.CategoryID || null, cost_code_id: row.CostCodeID || null,
    zone_id: row.ZoneID || null, division_id: row.DivisionID || null, layer_id: row.LayerID || null,
    item_name: row.ItemName, uom: row.UOM,
    material_cost_per_unit: numOrNull(row.MaterialCostPerUnit),
    labor_hr_per_unit: numOrNull(row.LaborHrPerUnit),
    vendor_sub_cost_per_unit: numOrZero(row.VendorSubCostPerUnit),
    // Not on the form — carried through so an edit doesn't reset them.
    trade: row.Trade || null,
    pricing_status: row.PricingStatus || null,
    source_vendor: row.SourceVendor || null,
    source_url: row.SourceUrl || null,
    is_editable: row.IsEditable == null ? null : (row.IsEditable ? 1 : 0),
    ...mappingAndPricingPayload(row),
    ...drawableFieldsPayload(row),
  };
}

export function assemblySavePayload(row) {
  return {
    assembly_id: row.AssemblyID ?? null, code: row.Code, category_id: row.CategoryID || null, cost_code_id: row.CostCodeID || null,
    zone_id: row.ZoneID || null, division_id: row.DivisionID || null, layer_id: row.LayerID || null,
    description: row.Description, uom: row.UOM,
    material_cost_per_unit: numOrZero(row.MaterialCostPerUnit),
    labor_hr_per_unit: numOrZero(row.LaborHrPerUnit), default_labor_factor_id: row.DefaultLaborFactorID || null,
    quantity_formula_id: row.QuantityFormulaID || null, vendor_sub_cost_per_unit: numOrZero(row.VendorSubCostPerUnit),
    trade: row.Trade || null, // not on the form — carried through so an edit doesn't reset it to 'electrical'
    ...mappingAndPricingPayload(row),
    ...drawableFieldsPayload(row),
  };
}
